/*
 * Being told that a chat is now yours, while the panel is in another tab.
 *
 * The sound is the only thing that reaches an operator who is working in
 * something else, and a browser mutes a tab it has not been clicked in - so the
 * one case this exists for is the one case it fails. A notification from the
 * system does not depend on the tab being awake, and it names the chat that
 * has been handed over.
 *
 * Off until the operator turns it on, per browser: permission is the browser's
 * to give, and asking for it unprompted is how a panel gets blocked for good.
 */

const STORAGE_PREFIX = 'desktopNotifications-';

/*
 * What a click on one should open.
 *
 * The notification knows which chat it is about; opening it is the chat
 * screen's business - that is where the list, the route and the rule about not
 * taking a chat off the queue live. It registers itself while it is on screen,
 * and while it is not, a click does what it has always done: brings the panel
 * forward and leaves the operator where they were.
 */
let opener = null;

export function setConversationOpener(handler) {
    opener = handler;

    return () => {
        if (opener === handler) opener = null;
    };
}

/* the notification still on screen for each chat, by tag */
const live = {};

export function isSupported() {
    return typeof window !== 'undefined' && 'Notification' in window;
}

export function permission() {
    return isSupported() ? Notification.permission : 'denied';
}

export function isEnabled(userId) {
    if (!isSupported() || permission() !== 'granted') return false;

    try {
        return localStorage.getItem(STORAGE_PREFIX + userId) === 'true';
    } catch (e) {
        return false;
    }
}

/**
 * Turns them on or off for this operator, asking the browser the first time.
 * Resolves with what the setting actually ended up being - a refused permission
 * leaves it off.
 */
export function setEnabled(userId, enabled) {
    if (!isSupported()) return Promise.resolve(false);

    const remember = (value) => {
        try {
            localStorage.setItem(STORAGE_PREFIX + userId, value ? 'true' : 'false');
        } catch (e) {
            /* a browser that keeps nothing will ask again next time */
        }
        return value;
    };

    if (!enabled) return Promise.resolve(remember(false));

    if (permission() === 'granted') return Promise.resolve(remember(true));

    /*
     * Older browsers take a callback here and return nothing, newer ones return
     * a promise. Asked both ways so neither is left hanging.
     */
    return new Promise((resolve) => {
        const answer = Notification.requestPermission((result) => resolve(result));
        if (answer && typeof answer.then === 'function') answer.then(resolve);
    }).then(result => remember(result === 'granted'));
}

/*
 * Whether the operator can already see the panel, in which case the sound and
 * the chat itself have said everything a notification would.
 *
 * Hidden is not enough on its own: a browser calls its tab visible while it
 * sits behind somebody's CRM or mail client, which is precisely where an
 * operator is when they miss a message. The window also has to have the focus.
 */
function isLookingAtThePanel() {
    if (typeof document === 'undefined') return false;
    if (document.hidden) return false;

    return typeof document.hasFocus !== 'function' || document.hasFocus();
}

/**
 * Shows one, if this operator asked for them and is not already looking at the
 * panel. The tag is the chat, so a second notification about the same one
 * replaces the first rather than stacking beside it.
 */
export function notify(userId, {title, body, tag, conversationId}) {
    if (!isEnabled(userId)) return;
    if (isLookingAtThePanel()) return;

    try {
        /*
         * The one we last showed for this chat, closed by hand first.
         *
         * A tag means the next notification replaces the one before it rather
         * than stacking, which keeps one chat to one entry however often it
         * changes hands. What is easy to miss is that the one before it does not have
         * to be on screen: once it has slid away it lives on in the system's
         * notification centre, still holding the tag - and a replacement of
         * something already there is delivered quietly, with no toast and no
         * sound. That is the "it worked once and never again" case.
         *
         * So: the old one is closed, and renotify asks for the alert to be
         * raised again even when a replacement does happen.
         */
        if (live[tag]) {
            live[tag].close();
            delete live[tag];
        }

        const notification = new Notification(title, {body, tag, renotify: true});

        live[tag] = notification;

        notification.onclose = () => {
            if (live[tag] === notification) delete live[tag];
        };

        notification.onclick = () => {
            window.focus();
            notification.close();
            delete live[tag];

            if (opener && conversationId) opener(conversationId);
        };
    } catch (e) {
        /* some browsers refuse to construct one outside a service worker */
    }
}



// WEBPACK FOOTER //
// ./src/utils/desktopNotifications.js